import React from 'react';
import { useHistory } from 'react-router-dom';
import { Menu, Dropdown } from 'antd';
import { RECORD_FIELD } from 'react-structure-admin';
import UserProfileImage from 'components/UserProfileImage';
import { useDispatch, useSelector } from 'react-redux';
import {
  updatePassword,
  currentUserGet,
  getMyPreferences,
  createCompany,
  getCompany
} from 'stores/users/userActions';

import UpdatePassword from 'pages/shared/UpdatePassword';
import CurrentUserEdit from 'pages/shared/CurrentUserEdit';
import CurrentUserMyPreferences from 'pages/shared/CurrentUserMyPreferences';
import useAuth from 'core/hooks/useAuth';
import { reducerName } from 'util/normalizers';
import usePermissions from 'core/hooks/usePermissions';
import { changeTenant } from 'stores/auth/authActions';
import PatientFinancialEntryList from 'pages/shared/PatientFinancialEntryList';
import CompanyEdit from 'pages/shared/CompanyEdit';
import { getCurrentProfessional } from 'stores/healthServiceProviders/healthServiceProviderActions';
import CurrentProfessionalEdit from 'pages/shared/CurrentProfessionalEdit';
import { useCustomHistory } from 'hooks';

const CurrentUserProfile = () => {
  const {
    user,
    company,
    isAdmin,
    userCompany,
    isMultiTenant,
    tenantId
  } = useAuth();
  const {
    isProfessional,
    isStudent,
    isUserAdmin,
    isPartnership
  } = usePermissions();
  const dispatch = useDispatch();
  const history = useCustomHistory();
  const {
    editingCurrentUser,
    [RECORD_FIELD]: resourceUsers,
    myPreferences,
    saving
  } = useSelector((state) => state.resources.users || {});

  const signout = () => {
    history.push('/signout', false);
  };

  const handleEditProfile = () => {
    dispatch(currentUserGet(user.id));
  };

  const handleEditProfessional = () => {
    dispatch(getCurrentProfessional());
  };

  const handleUpdatePassword = () => {
    dispatch(updatePassword({ userId: user.id }));
  };

  const handleMyPreferences = () => {
    dispatch(getMyPreferences());
  };

  const handleMySubscription = () => {
    history.push('current-subscriptions');
  };

  const handleBackToRootTenant = () => {
    dispatch(
      changeTenant(null, () => {
        history.push('/a/accounts/professional-accounts', false);
      })
    );
  };

  const handleEditingCompany = () => {
    if (!userCompany) {
      dispatch(createCompany());
    } else {
      dispatch(getCompany(userCompany.id));
    }
  };

  const allowCreateCompany = () => {
    return isProfessional && company?.settings?.allowToCreateOtherCompanies;
  };

  const changeTenantClickHandle = () => {
    dispatch(
      changeTenant(null, () => {
        history.push('/accounts-chooser', false);
      })
    );
  };

  const userMenuOptions = (
    <Menu>
      <Menu.Item onClick={handleEditProfile}>Meus dados</Menu.Item>
      {isProfessional ? (
        <Menu.Item onClick={handleEditProfessional}>
          Meus dados profissionais
        </Menu.Item>
      ) : null}
      {isProfessional || isAdmin() ? (
        <Menu.Item onClick={handleMyPreferences}>Minhas preferências</Menu.Item>
      ) : null}
      {allowCreateCompany() && (
        <Menu.Item onClick={handleEditingCompany}>Meus dados (NFS-e)</Menu.Item>
      )}
      {(isUserAdmin || isPartnership || isAdmin()) && tenantId && (
        <Menu.Item onClick={handleMySubscription}>Minha assinatura</Menu.Item>
      )}
      {isMultiTenant && tenantId ? (
        <Menu.Item onClick={changeTenantClickHandle}>Trocar de conta</Menu.Item>
      ) : null}
      <Menu.Item onClick={handleUpdatePassword}>Alterar senha</Menu.Item>
      {isAdmin() && (
        <Menu.Item onClick={handleBackToRootTenant}>Administração</Menu.Item>
      )}
      <Menu.Item onClick={signout}>Sair</Menu.Item>
    </Menu>
  );

  const userName = user?.professionalName ?? user?.displayName ?? user?.name;
  const name = reducerName(userName);

  return (
    <>
      <Dropdown overlay={userMenuOptions} trigger={['click']}>
        <div className="gx-flex-row gx-align-items-center gx-avatar-row gx-flex-nowrap">
          <UserProfileImage
            className="gx-size-40 gx-pointer gx-mr-2"
            id={user.id}
            uri={user.profileImage?.uri}
          />

          <div>
            <span className="gx-avatar-name gx-text-white">
              {name || <span style={{ color: '#ccc' }}>Carregando...</span>}
              <i className="icon gx-text-white icon-chevron-down gx-fs-xxs gx-ml-2" />
            </span>
            {isStudent ? (
              <div className="gx-avatar-name gx-fs-11 gx-text-white">
                Estudante
              </div>
            ) : null}
            {isPartnership ? (
              <div className="gx-avatar-name gx-fs-11 gx-text-white">
                Parceiro
              </div>
            ) : null}
          </div>
        </div>
      </Dropdown>
      {editingCurrentUser ? <CurrentUserEdit /> : null}
      <CurrentProfessionalEdit />
      <PatientFinancialEntryList />
      <CompanyEdit />
      {resourceUsers && resourceUsers.editingPassword && (
        <UpdatePassword updatePassword={resourceUsers.editingPassword} />
      )}
      {myPreferences ? (
        <CurrentUserMyPreferences data={myPreferences} saving={saving} />
      ) : null}
    </>
  );
};

export default CurrentUserProfile;
